<template>
    <div
        ref="root"
        class="status-toast"
        :class="[severity, { notice: !severity, 'cursor-pointer': closesOnClick }]"
        @focusin="rememberOrigin"
    >
        <div v-if="severity" class="status-indicator mb-4">
            <div class="pulse-dot">
                <svg v-if="severity === 'success'" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6.25001 10L8.75001 12.5L13.75 7.5M18.3333 10C18.3333 14.6024 14.6024 18.3333 10 18.3333C5.39763 18.3333 1.66667 14.6024 1.66667 10C1.66667 5.39762 5.39763 1.66666 10 1.66666C14.6024 1.66666 18.3333 5.39762 18.3333 10Z" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
                <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 6.66667V10M10 13.3333H10.0083M18.3333 10C18.3333 14.6024 14.6024 18.3333 10 18.3333C5.39763 18.3333 1.66667 14.6024 1.66667 10C1.66667 5.39763 5.39763 1.66667 10 1.66667C14.6024 1.66667 18.3333 5.39763 18.3333 10Z" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </div>
        </div>

        <!-- A status toast is an alert, and the alert is its words alone: its controls stay outside it, or the dismiss
             control's "Dismiss: …" name would be read out as part of what happened. A notice is said through the app's
             announcer instead, so it takes no role. -->
        <div class="text-container" :role="severity ? 'alert' : undefined">
            <p v-if="title">{{ title }}</p>
            <span v-if="text" class="pb-supporting">{{ text }}</span>
        </div>

        <button
            v-if="action"
            type="button"
            class="p-btn-root_primary p-btn-root_size_sm shrink-0 whitespace-nowrap"
            @click.stop="action.onPress()"
        >
            {{ action.label }}
        </button>
        <!-- The name repeats the text because several toasts can never be told apart by the word "Dismiss" alone. -->
        <button type="button" class="toast-close" :aria-label="`Dismiss: ${text || title}`" @click.stop="emit('close-toast')">
            <PhX :size="14" weight="bold" aria-hidden="true" />
        </button>
    </div>
</template>
<script setup lang="ts">
import { PhX } from '@phosphor-icons/vue';

export type ToastSeverity = 'success' | 'error' | 'warning';

export interface StatusToastProps {
    severity?: ToastSeverity;
    title?: string;
    text?: string;
    action?: { label: string; onPress: () => void };
    /** Whether a click anywhere on the toast closes it. `useGlobalToast` decides, and gives the library the same answer. */
    closesOnClick: boolean;
}

withDefaults(defineProps<StatusToastProps>(), {
    title: '',
    text: ''
});

// vue-toastification closes the toast that rendered this component when it emits this.
const emit = defineEmits<{ 'close-toast': [] }>();

const root = ref<HTMLElement | null>(null);
// Where focus came from. A toast arrives without taking focus, so nothing else knows what the keyboard user was on
// before they tabbed over to deal with it.
let origin: HTMLElement | null = null;

function rememberOrigin(event: FocusEvent) {
    const from = event.relatedTarget;
    // Only on the way in: moving between the toast's own buttons must not overwrite it.
    if (from instanceof HTMLElement && !root.value?.contains(from)) origin = from;
}

// Closing a toast takes away the button that was activated. Without this, focus falls to the document and the
// keyboard user restarts at the top of the page. vue-toastification keeps the toast in the document through its leave
// transition, so the button still holds focus when this runs.
onBeforeUnmount(() => {
    if (root.value?.contains(document.activeElement) && origin?.isConnected) origin.focus();
});
</script>
<style scoped>
.status-toast {
    @apply flex items-start gap-3;
}

/* A notice is a line of text beside its controls, so the row centers on it. */
.status-toast.notice {
    @apply items-center;
}

.status-indicator {
    @apply w-5 h-5 items-center justify-center flex;
}

/*
    The mark stays small and the target takes the touch size around it (DESIGN.md, "A touch target no bigger than its
    mark"). The target reaches into the toast's own padding, so it neither grows the row nor pulls the mark in from the
    corner, and stops short of the toast's clipped edge (vue-toastification sets overflow: hidden) by enough to keep
    the focus ring whole. Centered on a line of text, it sits level with the title of a status toast and with the
    sentence of a notice.
*/
.toast-close {
    @apply flex shrink-0 items-center justify-center;
    color: var(--ink-muted);
    width: var(--touch-target-min);
    height: var(--touch-target-min);
    margin-block: calc((var(--leading-sm) - var(--touch-target-min)) / 2);
    margin-inline-end: calc((var(--leading-sm) - var(--touch-target-min)) / 2);
}

.toast-close:hover {
    color: var(--ink);
}

.text-container {
    @apply flex flex-1 flex-col items-start gap-1 min-w-0 break-words;

    & > p {
        @apply text-sm font-semibold text-zinc-900;
    }
}

.pulse-dot {
    @apply relative flex items-center justify-center;
}

.pulse-dot svg {
    @apply w-5 h-5;
}

/* The mark is a 20px icon, so the rings travel the icon distance (tokens/effects.css, "The attention pulse"). */
.pulse-dot::before,
.pulse-dot::after {
    content: '';
    --pulse-travel: 200%;
    @apply absolute rounded-full transform -translate-x-1/2 -translate-y-1/2;
    @apply w-3/4 h-3/4 border-2 absolute top-1/2 left-1/2;
    animation: pb-pulse var(--pulse-duration) var(--pulse-ease) infinite;
}

.pulse-dot::after {
    animation-delay: var(--pulse-offset);
}

.status-toast.success svg path {
    @apply stroke-success-600;
}
.status-toast.success .pulse-dot::before,
.status-toast.success .pulse-dot::after {
    @apply border-success-500;
}

.status-toast.error svg path {
    @apply stroke-red-700;
}
.status-toast.error .pulse-dot::before,
.status-toast.error .pulse-dot::after {
    @apply border-red-700;
}

.status-toast.warning svg path {
    @apply stroke-[var(--warning-mark)];
}
.status-toast.warning .pulse-dot::before,
.status-toast.warning .pulse-dot::after {
    @apply border-[var(--warning-mark)];
}
</style>
